import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Navbar from '../components/Navbar';
import CatalogueOTPModal from '../components/CatalogueOTPModal';
import CatalogueViewer from '../components/CatalogueViewer';
import { studioContent } from '../data/studioContent';
import { TYPOGRAPHY, PLACEHOLDER_IMAGES } from '../constants/typography';

gsap.registerPlugin(ScrollTrigger);

const StudiosPage = () => {
    const navigate = useNavigate();
    const [selectedStudio, setSelectedStudio] = useState(null);
    const [showOTPModal, setShowOTPModal] = useState(false);
    const [showViewer, setShowViewer] = useState(false);
    const [accessToken, setAccessToken] = useState(null);
    const [activeFilter, setActiveFilter] = useState('all');

    // Icon rendering helper
    const renderIcon = (iconName, size = 24) => {
        const icons = {
            lock: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
            furniture: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 9V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v3"/><path d="M2 11h20"/><path d="M2 16h20"/><path d="M5 11v10"/><path d="M19 11v10"/></svg>,
            palette: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="13.5" cy="6.5" r=".5"/><circle cx="17.5" cy="10.5" r=".5"/><circle cx="8.5" cy="7.5" r=".5"/><circle cx="6.5" cy="12.5" r=".5"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/></svg>,
            thread: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>,
            wood: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/></svg>,
            ceiling: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
            stone: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2.7 10.3a2.41 2.41 0 0 0 0 3.41l7.59 7.59a2.41 2.41 0 0 0 3.41 0l7.59-7.59a2.41 2.41 0 0 0 0-3.41l-7.59-7.59a2.41 2.41 0 0 0-3.41 0L2.7 10.3z"/></svg>,
            hardware: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>,
            flooring: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>,
            target: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
            mail: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
            eye: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
        };
        return icons[iconName] || icons.lock;
    };

    const studios = [
	    {
            id: 'lumi',
            name: 'LumiCeiling',
            fullName: 'LumiCeiling - Elevating Ceilings into Design Statements',
            tagline: 'Where ceilings become statements',
            icon: 'ceiling',
            desc: 'LumiCeiling is a hub dedicated to transforming ceilings into expressions of refined design and architectural elegance. We bring together innovation, precision materials, and contemporary aesthetics to redefine how ceilings enhance interior spaces.',
            image: PLACEHOLDER_IMAGES.aether,
            category: 'Ceiling Systems',
            products: ['Sophisticated Soffit & Gypsum Solutions', 'Precision Aluminium Profiles & Frames', 'Gypsum Boards & Powder', 'Modern Interior Ceiling Systems'],
            designs: 'Architectural Solutions',
            color: '#B8935A',
            keyFeatures: [
                'Premium soffit and gypsum ceiling solutions',
                'High-quality gypsum boards and gypsum powder',
                'Precision-engineered aluminium profiles and frames',
                'Designed for durability, aesthetics, and ease of installation',
                'Ideal for residential and commercial interiors'
            ],
            detailedDesc: 'Designed for residential, commercial, and architectural projects, LumiCeiling solutions combine form, function, and durability to create visually striking and lasting ceiling systems.'
        },
        {
            id: 'locknkey',
            name: 'LockNKey',
            fullName: 'LockNKey - Precision That Moves with Grace',
            tagline: 'Where precision meets perfection',
            icon: 'hardware',
            desc: 'LockNKey elevates interiors with precision-engineered hardware, designed to ensure flawless movement, secure functionality, and lasting grace. By blending advanced engineering with refined aesthetics, we deliver hardware solutions that enhance both the performance and appearance of modern spaces.',
            image: PLACEHOLDER_IMAGES.torque,
            category: 'Hardware & Fixtures',
            products: ['Door Handles & Knobs', 'Advanced Locks & Security', 'Smooth Motion Hardware', 'Premium Finishing Solutions'],
            designs: 'Precision Hardware',
            color: '#C9A96E',
            keyFeatures: [
                'Precision-crafted door handles and knobs',
                'Reliable automatic and mortise locking systems',
                'Smooth-action tandem boxes and 360-degree hinges',
                'Durable materials with refined finishing',
                'Designed for modern residential and commercial interiors'
            ],
            detailedDesc: 'From everyday living to high-traffic commercial environments, LockNKey hardware is crafted for reliability, smooth operation, and enduring quality.'
        },	
        {
            id: 'shape',
            name: 'ShapeStudio',
            fullName: 'ShapeStudio - Where Vision Takes Solid Form',
            tagline: 'Precision crafted, purpose built',
            icon: 'stone',
            desc: 'ShapeStudio brings ideas to life by giving vision solid form through expert CNC artistry and deep raw material mastery. Blending advanced precision technology with skilled craftsmanship, we create designs that are accurate, expressive, and built to last.',
            image: PLACEHOLDER_IMAGES.carve,
            category: 'CNC & Materials',
            products: ['Precision CNC Art Works', 'Quality MDF & Particle Boards', 'Custom Interior Solutions', 'Architectural Project Components'],
            designs: 'Precision Crafted',
            color: '#D4AF37',
            keyFeatures: [
                'Expert CNC cutting and artistic detailing',
                'High-quality MDF and particle boards',
                'Precision-driven craftsmanship and consistency',
                'Custom solutions for interior and architectural projects',
                'Reliable materials and refined finishes'
            ],
            detailedDesc: 'From concept-driven detailing to large-scale production, ShapeStudio delivers solutions that meet the evolving needs of modern interiors and architectural projects.'
        },
        {
            id: 'craftcove',
            name: 'CraftCove',
            fullName: 'CraftCove - Shaping Timeless Wooden Interiors',
            tagline: 'Where craftsmanship shapes interiors',
            icon: 'wood',
            desc: 'CraftCove is a trusted source for exquisite wood, panels, and doors, dedicated to shaping interiors that stand the test of time. Rooted in craftsmanship and quality, we bring together natural materials and refined finishes to create spaces defined by warmth, strength, and elegance.',
            image: PLACEHOLDER_IMAGES.chisel,
            category: 'Wood & Panels',
            products: ['Premium Teak Wood & Solid Wood', 'Wooden Door Frames & Doors', 'Plywoods & Engineered Woods', 'Decorative Laminates & Décor Sheets'],
            designs: 'Timeless Collections',
            color: '#C9A96E',
            keyFeatures: [
                'Premium teak wood and solid wood materials',
                'High-quality wooden door frames and readymade doors',
                'Reliable plywoods and engineered wood solutions',
                'Stylish laminates and décor sheets',
                'Materials crafted for durability, design, and timeless appeal'
            ],
            detailedDesc: 'From structural essentials to decorative surfaces, CraftCove offers complete wooden solutions for residential, commercial, and architectural interiors.'
        },	
        {
            id: 'thread',
            name: 'Thread',
            fullName: 'THEAD - Crafting Comfort, Defining Style',
            tagline: 'Where interiors come together beautifully',
            icon: 'thread',
            desc: 'THEAD is a hub for transforming interiors into warm, elegant, and inviting spaces. With a refined selection of sumptuous rugs, elegant blinds, and plush soft furnishings, we bring together comfort, craftsmanship, and contemporary design to enhance every interior.',
            image: PLACEHOLDER_IMAGES.thread,
            category: 'Textiles & Soft Furnishing',
            products: ['Premium Carpets & Rugs', 'Elegant Curtains & Blinds', 'Plush Soft Furnishings', 'Interior Enhancement Solutions'],
            designs: 'Comfort Collections',
            color: '#D4AF37',
            keyFeatures: [
                'Premium carpets and rugs for timeless interiors',
                'Elegant curtains and blinds with refined finishing',
                'Plush soft furnishings for added comfort and style',
                'Designs that balance beauty, durability, and function',
                'Suitable for residential and commercial interiors'
            ],
            detailedDesc: 'Designed for homes, offices, and commercial spaces, THEAD products blend aesthetics with functionality to create environments that feel as good as they look.'
        },	
        {
            id: 'surface',
            name: 'SurfaceStyle',
            fullName: 'SurfaceStyle - The Perfect Base for Brilliance',
            tagline: 'Where great design begins underfoot',
            icon: 'flooring',
            desc: 'SurfaceStyle is dedicated to crafting the perfect foundation for inspired interiors. With an exquisite range of flooring materials, we bring together design, durability, and innovation to create surfaces that elevate every space.',
            image: PLACEHOLDER_IMAGES.strata,
            category: 'Flooring Solutions',
            products: ['Elegant Tiles for Every Space', 'Natural & Engineered Wood Flooring', 'Floor-Based Paints', 'Premium Surface Solutions'],
            designs: 'Foundation Collections',
            color: '#B8935A',
            keyFeatures: [
                'Premium tiles with refined designs',
                'High-quality wood flooring solutions',
                'Durable and elegant floor-based paints',
                'Materials crafted for longevity and performance',
                'Suitable for residential and commercial interiors'
            ],
            detailedDesc: 'From timeless classics to contemporary finishes, SurfaceStyle flooring solutions are designed to enhance the beauty, comfort, and performance of residential and commercial interiors.'
        },		
        {
            id: 'globalnest',
            name: 'GlobalNest',
            fullName: 'Global Nest - Your Worldwide Furniture Sourcing Hub',
            tagline: 'Connecting the world through furniture',
            icon: 'furniture',
            desc: 'Global Nest is the hub for connecting you to premium furniture solutions across the globe. We specialize in worldwide furniture sourcing and customized OEM/ODM creations, helping brands, retailers, and projects bring their furniture concepts to life with precision, quality, and reliability.',
            image: PLACEHOLDER_IMAGES.nexus,
            category: 'Furniture Sourcing',
            products: ['OEM/ODM Furniture Manufacturing', 'Global Sourcing Solutions', 'Custom Design & Development', 'Quality Assurance & Compliance'],
            designs: 'Worldwide Network',
            color: '#C9A96E',
            keyFeatures: [
                'Worldwide furniture sourcing under one roof',
                'Customized OEM/ODM furniture manufacturing',
                'Strong global supplier and factory network',
                'Quality assurance and international compliance',
                'End-to-end sourcing, development, and logistics support'
            ],
            detailedDesc: 'With a strong global supplier network, Global Nest bridges the gap between design vision and manufacturing excellence. From residential and commercial furniture to bespoke collections, we source, develop, and deliver furniture that meets international standards and tailored specifications.'
        },
        {
            id: 'artistry',
            name: 'ArtistryRoom',
            fullName: 'ArtistryRoom - Discovering Decorative Elegance',
            tagline: 'Where artistry shapes your space',
            icon: 'palette',
            desc: 'ArtistryRoom is where design meets craftsmanship, curating decorative pieces that elevate everyday spaces into expressions of refined taste. Rooted in creativity and inspired by timeless aesthetics, we bring together iconic artifacts and sleek modern forms to create décor that speaks of elegance, balance, and individuality.',
            image: PLACEHOLDER_IMAGES.muse,
            category: 'Decorative Elements',
            products: ['Signature Décor & Artifacts', 'Sleek Metal Forms', 'PVC Finishing Solutions', 'Modern Space Design'],
            designs: 'Curated Collections',
            color: '#B8955A',
            keyFeatures: [
                'Premium signature décor and artistic artifacts',
                'Sleek metal designs with durable PVC finishing',
                'Thoughtfully curated for modern and elegant spaces',
                'Perfect balance of aesthetics and functionality',
                'Crafted to elevate everyday interiors'
            ],
            detailedDesc: 'From statement accents to functional artistry, every ArtistryRoom creation is thoughtfully designed to complement both contemporary and classic interiors.'
        },
    ];

    const categories = ['all', 'Global Nest', 'ArtistryRoom', 'THEAD', 'CraftCove', 'LumiCeiling', 'ShapeStudio', 'LockNKey', 'SurfaceStyle'];

    const filteredStudios = activeFilter === 'all' 
        ? studios 
        : studios.filter(studio => studio.name === activeFilter);

    useEffect(() => {
        // Animate hero section
        gsap.from('.studios-hero-title', {
            y: 50,
            duration: 1,
            ease: 'power3.out'
        });

        gsap.from('.studios-hero-subtitle', {
            y: 30,
            duration: 1,
            delay: 0.2,
            ease: 'power3.out'
        });

        gsap.from('.hero-stats', {
            y: 40,
            duration: 1,
            delay: 0.4,
            ease: 'power3.out'
        });

        // Animate filter buttons
        gsap.from('.filter-button', {
            scrollTrigger: {
                trigger: '.filter-section',
                start: 'top 80%',
            },
            y: 30,
            duration: 0.6,
            stagger: 0.1,
            ease: 'power2.out'
        });

        // Animate studio cards with delay to ensure DOM is ready
        const animateCards = setTimeout(() => {
            const cards = document.querySelectorAll('.studio-card');
            if (cards.length > 0) {
                gsap.fromTo('.studio-card', 
                    {
                        y: 30,
                        opacity: 0
                    },
                    {
                        scrollTrigger: {
                            trigger: '.studios-grid-section',
                            start: 'top 85%',
                            toggleActions: 'play none none none',
                            once: false
                        },
                        y: 0,
                        opacity: 1,
                        duration: 0.6,
                        stagger: 0.08,
                        ease: 'power2.out',
                        immediateRender: true
                    }
                );
            }
        }, 100);

        return () => {
            clearTimeout(animateCards);
            ScrollTrigger.getAll().forEach(trigger => trigger.kill());
        };

        // Animate How It Works section
        gsap.from('.how-it-works-step', {
            scrollTrigger: {
                trigger: '.how-it-works-section',
                start: 'top 70%',
            },
            y: 50,
            duration: 0.8,
            stagger: 0.2,
            ease: 'power2.out'
        });

        // Animate CTA section
        gsap.from('.cta-section-content', {
            scrollTrigger: {
                trigger: '.cta-section',
                start: 'top 80%',
            },
            scale: 0.95,
            duration: 1,
            ease: 'power3.out'
        });
    }, [filteredStudios]);

    const handleStudioClick = (studio) => {
        setSelectedStudio(studio);
        setShowOTPModal(true);
    };

    const handleStudioImageClick = (studio, event) => {
        // Stop event propagation to prevent triggering the card click
        event.stopPropagation();
        
        // Navigate to studio detail page
        const studioSlug = studio.name.toLowerCase().replace(/\s+/g, '-');
        navigate(`/studio/${studioSlug}`);
    };

    const handleOTPSuccess = (token, studioId, studioName) => {
        setAccessToken(token);
        setShowOTPModal(false);
        setShowViewer(true);
    };

    const handleCloseViewer = () => {
        setShowViewer(false);
        setAccessToken(null);
        setSelectedStudio(null);
    };

    const handleCloseOTPModal = () => {
        setShowOTPModal(false);
        setSelectedStudio(null);
    };

    return (
        <div>
            <Helmet>
                <title>Our 8 Design Studios - Hav'n Interiors</title>
                <meta name="description" content="Explore our 8 specialized design studios. Each studio offers unique catalogues featuring premium products and materials." />
                <meta name="keywords" content="design studios, interior design, catalogues, luxury materials, furniture, textiles" />
            </Helmet>

            <Navbar />

            {/* Hero Section */}
            <section className="hero" style={{ 
                height: '70vh',
                background: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=1600&q=80)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed',
                position: 'relative',
                overflow: 'hidden',
                paddingTop: '120px'
            }}>
                {/* Animated Background Pattern */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(201, 169, 110, 0.08) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(184, 147, 90, 0.08) 0%, transparent 50%)',
                    opacity: 1
                }} />
                
                <div className="hero-content" style={{ position: 'relative', zIndex: 2, textShadow: '0 2px 10px rgba(0, 0, 0, 0.5)', paddingTop: '40px' }}>
                    <div style={{ marginBottom: '5px' }}>
                        <span style={{ 
                            fontSize: '16px',
                            color: 'var(--accent-color)',
                            letterSpacing: '3px',
                            textTransform: 'uppercase',
                            fontWeight: '600'
                        }}>
                            Premium Design Solutions
                        </span>
                    </div>
                    
                    <h1 className="studios-hero-title" style={{ 
                        fontSize: '72px',
                        fontWeight: '300',
                        marginBottom: '25px',
                        letterSpacing: '-2px',
                        lineHeight: '1.1',
                        color: 'rgba(245, 241, 237, 1)',
                        marginTop: '0'
                    }}>
                        Our 8 Design<br />
                        <span style={{ color: 'var(--accent-color)' }}>Studios</span>
                    </h1>
                    
                    <p className="studios-hero-subtitle" style={{
                        fontSize: '20px',
                        color: '#F5F1ED',
                        maxWidth: '600px',
                        margin: '0 auto 40px',
                        lineHeight: '1.6',
                        fontWeight: '400'
                    }}>
                        From global furniture sourcing to precision hardware,<br />
                        each studio delivers specialized excellence and premium materials
                    </p>

                    <div className="hero-stats responsive-flex" style={{
                        display: 'flex',
                        gap: '20px',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginTop: '50px',
                        flexWrap: 'wrap'
                    }}>
                        <div className="stat-card" style={{ textAlign: 'center', minWidth: '150px' }}>
                            <div style={{ fontSize: '36px', fontWeight: '300', color: 'var(--accent-color)' }}>8</div>
                            <div style={{ fontSize: '12px', color: 'rgba(245, 241, 237, 0.9)', letterSpacing: '1px', fontWeight: '500' }}>SPECIALIZED STUDIOS</div>
                        </div>
                        <div className="stat-divider" style={{ width: '1px', height: '40px', background: 'rgba(201, 169, 110, 0.5)' }} />
                        <div className="stat-card" style={{ textAlign: 'center', minWidth: '150px' }}>
                            <div style={{ fontSize: '36px', fontWeight: '300', color: 'var(--accent-color)' }}>GLOBAL</div>
                            <div style={{ fontSize: '12px', color: 'rgba(245, 241, 237, 0.9)', letterSpacing: '1px', fontWeight: '500' }}>SOURCING NETWORK</div>
                        </div>
                        <div className="stat-divider" style={{ width: '1px', height: '40px', background: 'rgba(201, 169, 110, 0.5)' }} />
                        <div className="stat-card" style={{ textAlign: 'center', minWidth: '150px' }}>
                            <div style={{ fontSize: '36px', fontWeight: '300', color: 'var(--accent-color)' }}>PREMIUM</div>
                            <div style={{ fontSize: '12px', color: 'rgba(245, 241, 237, 0.9)', letterSpacing: '1px', fontWeight: '500' }}>QUALITY ASSURED</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Studios Section - Filters & Grid Combined */}
            <section className="studios-grid-section" style={{ 
                background: '#0F0F0F',
                padding: '60px 5% 100px',
                minHeight: '100vh',
                position: 'relative',
                zIndex: 2
            }}>
                <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                   

                    {/* Studios Grid */}
                    <div className="responsive-grid grid-3" style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', 
                        gap: '30px',
                        justifyContent: 'start'
                    }}>
                        {filteredStudios.map((studio, index) => (
                            <div
                                key={studio.id}
                                className="studio-card"
                                onClick={() => handleStudioClick(studio)}
                                style={{
                                    background: '#1A1A1A',
                                    borderRadius: '20px',
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                    border: '1px solid rgba(201, 169, 110, 0.3)',
                                    position: 'relative',
                                    zIndex: 3,
                                    transform: 'translateY(0) scale(1)',
                                    willChange: 'transform',
                                    opacity: 1
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-10px) scale(1.01)';
                                    e.currentTarget.style.boxShadow = '0 25px 50px rgba(201, 169, 110, 0.4)';
                                    e.currentTarget.style.borderColor = 'var(--accent-color)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                    e.currentTarget.style.boxShadow = 'none';
                                    e.currentTarget.style.borderColor = 'rgba(201, 169, 110, 0.3)';
                                }}
                            >
                                {/* Studio Image */}
                                <div 
                                    onClick={(e) => handleStudioImageClick(studio, e)}
                                    style={{
                                        height: '280px',
                                        background: `linear-gradient(135deg, rgba(201, 169, 110, 0.3) 0%, rgba(0, 0, 0, 0.5) 100%), url(${studio.image})`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        position: 'relative',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '20px',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'scale(1.02)';
                                        e.currentTarget.style.filter = 'brightness(1.1)';
                                        const overlay = e.currentTarget.querySelector('.click-overlay');
                                        if (overlay) overlay.style.opacity = '1';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'scale(1)';
                                        e.currentTarget.style.filter = 'brightness(1)';
                                        const overlay = e.currentTarget.querySelector('.click-overlay');
                                        if (overlay) overlay.style.opacity = '0';
                                    }}
                                >
                                    {/* Catalogue Badge with Icon */}
                                    <div style={{
                                        position: 'absolute',
                                        top: '20px',
                                        right: '20px',
                                        background: 'rgba(0, 0, 0, 0.8)',
                                        backdropFilter: 'blur(10px)',
                                        padding: '10px 20px',
                                        borderRadius: '25px',
                                        fontSize: '13px',
                                        color: 'var(--accent-color)',
                                        fontWeight: '600',
                                        border: '1px solid var(--accent-color)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        zIndex: 2
                                    }}>
                                        {renderIcon(studio.icon, 20)} <span>Catalogue</span>
                                    </div>

                                    {/* Click to View Overlay */}
                                    <div style={{
                                        position: 'absolute',
                                        top: '20px',
                                        left: '20px',
                                        background: 'rgba(201, 169, 110, 0.9)',
                                        backdropFilter: 'blur(10px)',
                                        padding: '8px 16px',
                                        borderRadius: '20px',
                                        fontSize: '12px',
                                        color: '#000000',
                                        fontWeight: '600',
                                        opacity: 0,
                                        transition: 'opacity 0.3s ease',
                                        zIndex: 2,
                                        pointerEvents: 'none'
                                    }}
                                    className="click-overlay"
                                    >
                                        Click to View Details
                                    </div>

                                    {/* Content Wrapper - Centered */}
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textAlign: 'center',
                                        width: '100%',
                                        zIndex: 1
                                    }}>
                                        {/* Studio Name */}
                                        <h3 style={{ 
                                            fontSize: '42px', 
                                            color: '#fff', 
                                            fontWeight: '300',
                                            letterSpacing: '1px',
                                            textShadow: '0 4px 20px rgba(0, 0, 0, 0.8)',
                                            margin: '0 0 12px 0',
                                            lineHeight: '1.2'
                                        }}>
                                            {studio.name}
                                        </h3>

                                        {/* Category Tag */}
                                        <div style={{
                                            background: 'rgba(201, 169, 110, 0.3)',
                                            backdropFilter: 'blur(10px)',
                                            padding: '8px 18px',
                                            borderRadius: '20px',
                                            fontSize: '12px',
                                            color: 'rgba(245, 241, 237, 1)',
                                            fontWeight: '500',
                                            border: '1px solid rgba(201, 169, 110, 0.3)',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {studio.category}
                                        </div>
                                    </div>
                                </div>

                                {/* Studio Details */}
                                <div style={{ padding: '35px 30px' }}>
                                    {/* Studio Tagline */}
                                    <div style={{ marginBottom: '20px' }}>
                                        <p style={{
                                            fontSize: '13px',
                                            color: 'rgba(245, 241, 237, 0.85)',
                                            fontStyle: 'italic',
                                            letterSpacing: '0.5px'
                                        }}>
                                            {studio.tagline}
                                        </p>
                                    </div>

                                    {/* Description */}
                                    <p style={{ 
                                        fontSize: '15px', 
                                        color: 'rgba(245, 241, 237, 0.95)',
                                        lineHeight: '1.7',
                                        marginBottom: '25px',
                                        minHeight: '50px'
                                    }}>
                                        {studio.desc}
                                    </p>

                                    {/* Key Products */}
                                    <div style={{
                                        marginBottom: '25px',
                                        paddingTop: '20px',
                                        borderTop: '1px solid rgba(201, 169, 110, 0.1)'
                                    }}>
                                        <div style={{ 
                                            fontSize: '12px',
                                            color: 'rgba(245, 241, 237, 0.8)',
                                            marginBottom: '12px',
                                            letterSpacing: '1px',
                                            textTransform: 'uppercase'
                                        }}>
                                            Key Products
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                            {studio.products.map((product, i) => (
                                                <span key={i} style={{
                                                    padding: '6px 14px',
                                                    background: 'rgba(201, 169, 110, 0.15)',
                                                    borderRadius: '15px',
                                                    fontSize: '12px',
                                                    color: 'rgba(245, 241, 237, 0.95)',
                                                    border: '1px solid rgba(201, 169, 110, 0.3)'
                                                }}>
                                                    {product}
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Design Count & CTA */}
                                    <div style={{ 
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        paddingTop: '20px',
                                        borderTop: '1px solid rgba(201, 169, 110, 0.1)'
                                    }}>
                                        <div>
                                            <div style={{ 
                                                fontSize: '18px',
                                                fontWeight: '500',
                                                color: 'var(--accent-color)',
                                                marginBottom: '4px'
                                            }}>
                                                {studio.designs}
                                            </div>
                                            <div style={{ 
                                                fontSize: '11px', 
                                                color: 'rgba(245, 241, 237, 0.75)',
                                                letterSpacing: '0.5px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px'
                                            }}>
                                                {renderIcon('lock', 16)} OTP Protected Access
                                            </div>
                                        </div>
                                        <div style={{
                                            width: '50px',
                                            height: '50px',
                                            borderRadius: '50%',
                                            background: 'linear-gradient(135deg, #C9A96E 0%, #B8935A 100%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '24px',
                                            color: '#000000',
                                            fontWeight: '300',
                                            transition: 'all 0.3s ease'
                                        }}>
                                            →
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <section className="how-it-works-section" style={{ 
                background: 'linear-gradient(135deg, #1a1a1a 0%, #0F0F0F 100%)',
                padding: '100px 5%',
                textAlign: 'center'
            }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                    <div style={{ marginBottom: '70px' }}>
                        <h2 style={{ 
                            fontSize: '48px', 
                            fontWeight: '300',
                            marginBottom: '20px',
                            letterSpacing: '-1px'
                        }}>
                            How to Access Catalogues
                        </h2>
                        <p style={{ 
                            fontSize: '18px',
                            color: 'rgba(245, 241, 237, 0.6)',
                            maxWidth: '600px',
                            margin: '0 auto'
                        }}>
                            Simple, secure, and instant access to our premium design collections
                        </p>
                    </div>

                    <div className="responsive-grid grid-3" style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(3, 1fr)', 
                        gap: '50px',
                        marginTop: '60px'
                    }}>
                        {[
                            {
                                step: '01',
                                icon: 'target',
                                title: 'Select Studio',
                                desc: 'Choose the studio catalogue you want to explore'
                            },
                            {
                                step: '02',
                                icon: 'mail',
                                title: 'Email Verification',
                                desc: 'Enter your email to receive instant 6-digit OTP'
                            },
                            {
                                step: '03',
                                icon: 'eye',
                                title: 'Browse Catalogue',
                                desc: 'View premium designs in our secure viewer'
                            }
                        ].map((item, index) => (
                            <div key={index} className="how-it-works-step" style={{
                                padding: '40px 30px',
                                background: 'rgba(201, 169, 110, 0.05)',
                                borderRadius: '20px',
                                border: '1px solid rgba(201, 169, 110, 0.1)',
                                transition: 'all 0.4s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(201, 169, 110, 0.1)';
                                e.currentTarget.style.transform = 'translateY(-10px)';
                                e.currentTarget.style.borderColor = 'var(--accent-color)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'rgba(201, 169, 110, 0.05)';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.borderColor = 'rgba(201, 169, 110, 0.1)';
                            }}
                            >
                                <div style={{ 
                                    marginBottom: '25px',
                                    color: 'var(--accent-color)',
                                    display: 'flex',
                                    justifyContent: 'center'
                                }}>
                                    {renderIcon(item.icon, 56)}
                                </div>
                                <div style={{
                                    fontSize: '14px',
                                    color: 'var(--accent-color)',
                                    fontWeight: '600',
                                    letterSpacing: '2px',
                                    marginBottom: '15px'
                                }}>
                                    STEP {item.step}
                                </div>
                                <h3 style={{ 
                                    fontSize: '24px',
                                    color: 'var(--text-light)',
                                    marginBottom: '15px',
                                    fontWeight: '400'
                                }}>
                                    {item.title}
                                </h3>
                                <p style={{ 
                                    fontSize: '15px',
                                    color: 'rgba(245, 241, 237, 0.7)',
                                    lineHeight: '1.7'
                                }}>
                                    {item.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta-section" style={{ 
                textAlign: 'center',
                background: 'linear-gradient(135deg, #C9A96E 0%, #B8935A 100%)',
                padding: '80px 5%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <div className="cta-section-content" style={{ maxWidth: '800px', margin: '0 auto' }}>
                    <h2 style={{ 
                        fontSize: '42px',
                        fontWeight: '300',
                        marginBottom: '25px',
                        color: '#000000',
                        letterSpacing: '-1px',
                        marginTop: '0'
                    }}>
                        Need Expert Consultation?
                    </h2>
                    <p style={{ 
                        fontSize: '18px',
                        color: 'rgba(0, 0, 0, 0.7)',
                        marginBottom: '35px',
                        lineHeight: '1.7',
                        marginTop: '0'
                    }}>
                        Our design experts are ready to help you choose the perfect materials<br />
                        and create spaces that inspire.
                    </p>
                    <Link 
                        to="/contact"
                        style={{
                            display: 'inline-block',
                            padding: '18px 50px',
                            background: '#000000',
                            color: '#C9A96E',
                            fontSize: '16px',
                            fontWeight: '600',
                            borderRadius: '50px',
                            textDecoration: 'none',
                            transition: 'all 0.3s ease',
                            border: '2px solid #000000'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.background = 'transparent';
                            e.target.style.color = '#000000';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.background = '#000000';
                            e.target.style.color = '#C9A96E';
                        }}
                    >
                        Contact Us
                    </Link>
                </div>
            </section>

            {/* Footer */}
            <footer>
                <img src={`${process.env.PUBLIC_URL}/current-logo.png`} alt="Hav'n Interiors" className="footer-logo" />
                <p>&copy; 2025 Hav'n Interiors & Global Sourcing. All Rights Reserved.</p>
                <p style={{ marginTop: '10px', fontSize: '14px', opacity: 0.6 }}>Innovate. Integrate. Execute.</p>
            </footer>

            {/* Modals */}
            {showOTPModal && selectedStudio && (
                <CatalogueOTPModal
                    studio={selectedStudio.id}
                    studioName={selectedStudio.fullName}
                    onClose={handleCloseOTPModal}
                    onSuccess={handleOTPSuccess}
                />
            )}

            {showViewer && selectedStudio && accessToken && (
                <CatalogueViewer
                    studio={selectedStudio.id}
                    studioName={selectedStudio.fullName}
                    token={accessToken}
                    onClose={handleCloseViewer}
                />
            )}
        </div>
    );
};


export default StudiosPage;
